import React, { SVGProps, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicPlans, submitDemoRequest } from '../services/api';
import { PlanPublic } from '../types';

type IconProps = SVGProps<SVGSVGElement>;
type LandingPlan = {
  id: string;
  name: string;
  tagline: string;
  monthly: number;
  yearly: number;
  currency?: string | null;
  ctaLabel?: string;
  highlight?: boolean;
  features: string[];
  priceLabel?: string;
  priceNote?: string;
};
type AuthMode = 'signin' | 'signup';
type LandingAuthMode = 'modal' | 'routes';
type LandingSection = 'use-cases' | 'pricing' | 'faq' | 'contact';

const fallbackLandingPlans: LandingPlan[] = [
  {
    id: 'trial',
    name: 'Trial',
    tagline: 'For first sites proving live telemetry',
    monthly: 0,
    yearly: 0,
    features: [
      '30-day workspace trial',
      'Connect MQTT devices',
      'Live dashboards and maps',
      'Email verification and invites',
      'Guided onboarding support',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    tagline: 'For small and medium monitoring',
    monthly: 49,
    yearly: 39,
    highlight: true,
    features: [
      'Multi-site device monitoring',
      'White-labelling',
      'Dashboard Builder up to 5 dashboards',
      '10 data-stream integrations',
      '3 users max',
      '$10 per additional data stream/month',
      'Scheduled reports',
      'Role-based team access',
      'Priority implementation support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large industrial deployments',
    monthly: 199,
    yearly: 159,
    features: [
      'Everything in Growth plan',
      '50 dashboards and 100 data streams',
      '5 users max',
      'Dedicated deployment support',
      'Advanced audit and retention',
      'Private infrastructure options',
      'Custom integrations and SLAs',
      'Solutions engineer on call',
    ],
  },
  {
    id: 'custom',
    name: 'Custom Solution',
    tagline: 'For companies with private infrastructure needs',
    monthly: 0,
    yearly: 0,
    ctaLabel: 'Choose Custom Plan',
    priceLabel: 'Custom',
    priceNote: 'tailored plan',
    features: [
      'On-premise or private cloud hosting',
      'Custom integrations with existing systems',
      'Advanced security and access controls',
      'Dedicated deployment and training support',
      'Custom retention, reporting, and SLA terms',
      'Multi-location rollout planning',
    ],
  },
];

const landingFeatureLabels: Record<string, string> = {
  overview: 'Operational overview and live sensors',
  dashboards: 'Dashboard Builder',
  map: 'Map and floor-plan monitoring',
  alerts: 'Alerts',
  devices: 'Device management',
  sites: 'Sites and areas',
  mqtt: 'MQTT bridges',
  onboarding: 'Device onboarding',
  export: 'Data export',
  reports: 'Scheduled reports',
  team: 'Role-based team access',
  audit: 'Audit log',
  settings: 'Workspace settings',
};

const knownLandingPlanCopy: Record<string, Pick<LandingPlan, 'tagline' | 'features' | 'highlight' | 'ctaLabel' | 'priceLabel' | 'priceNote'>> = {
  trial: fallbackLandingPlans[0],
  free: {
    tagline: 'For first sites proving live telemetry',
    features: fallbackLandingPlans[0].features,
    ctaLabel: 'Start for free',
  },
  growth: fallbackLandingPlans[1],
  enterprise: fallbackLandingPlans[2],
  custom: fallbackLandingPlans[3],
};

const landingPlanOrder = ['trial', 'free', 'growth', 'enterprise', 'custom'];

const faqItems = [
  {
    question: 'Do we need a technical team to start?',
    answer: 'No. Seedronics can guide setup, device onboarding, dashboards, alerts, and reports so operators and managers can start monitoring sites without needing to understand the underlying telemetry stack.',
  },
  {
    question: 'What can Seedronics monitor?',
    answer: 'Seedronics can monitor solar farms, BESS (battery energy storage) sites, water systems, cold rooms, manufacturing assets, generators, fuel tanks, GPS assets, facility status, environmental readings, alerts, and operational reports. The platform can adapt to custom sensor and gateway payloads from many industries.',
  },
  {
    question: 'How does the Growth plan work?',
    answer: 'Growth is designed for small and medium monitoring teams. It supports multi-site monitoring, white-labelling, up to 5 dashboards, 10 data-stream integrations, 3 users, scheduled reports, role-based access, and priority implementation support. Extra data streams can be added at $10 per additional data stream/month.',
  },
  {
    question: 'What counts as a data stream?',
    answer: 'A data stream is one monitored metric coming from a sensor, meter, controller, inverter, battery system, machine, or gateway. If one gateway sends several separate readings that need their own monitoring, alerts, reports, or dashboards, each reading is counted as its own data stream. For example, solar output, battery state of charge, and room temperature from the same gateway may count as three data streams.',
  },
  {
    question: 'Can we use our own logo and brand?',
    answer: 'Yes. Growth includes white-labelling so companies can customize the platform with their own logo, brand colours, and workspace identity while Seedronics powers the monitoring experience behind the scenes.',
  },
  {
    question: 'What changes on Enterprise?',
    answer: 'Enterprise includes Growth capabilities with higher operating limits: 50 dashboards, 100 data streams, and 5 users. It also adds stronger deployment support, advanced audit and retention, private infrastructure options, custom integrations, and SLA conversations.',
  },
  {
    question: 'Can Seedronics be hosted on-premise or privately?',
    answer: 'Yes. Companies that need on-premise hosting, private cloud deployment, stricter security controls, custom integrations, or special reporting and retention terms should use the Custom Solution option.',
  },
  {
    question: 'Can we see a demo before deciding?',
    answer: 'Yes. We can schedule a demo meeting to show how dashboards, alerts, reports, maps, usage analysis, and predictive maintenance workflows would look for your own sites, assets, and facilities.',
  },
];

function buildLandingPlans(publicPlans: PlanPublic[]): LandingPlan[] {
  const publicBySlug = new Map(publicPlans.map((plan) => [plan.slug, plan]));

  return fallbackLandingPlans.map((fallbackPlan) => {
    const plan = publicBySlug.get(fallbackPlan.id) || (fallbackPlan.id === 'trial' ? publicBySlug.get('free') : undefined);
    const known = knownLandingPlanCopy[fallbackPlan.id];
    const monthly = getLandingPriceNumber(plan?.price_monthly) ?? fallbackPlan.monthly;
    return {
      id: plan?.slug || fallbackPlan.id,
      name: fallbackPlan.name,
      tagline: known?.tagline || 'A Seedronics monitoring plan configured for your workspace needs.',
      monthly,
      yearly: monthly === 0 ? 0 : Math.round(monthly * 0.8),
      currency: plan?.currency || fallbackPlan.currency || 'USD',
      highlight: known?.highlight || fallbackPlan.id === 'growth',
      ctaLabel: known?.ctaLabel,
      priceLabel: known?.priceLabel,
      priceNote: known?.priceNote,
      features: known?.features || (plan ? getPublicPlanFeatures(plan) : fallbackPlan.features),
    };
  }).sort((a, b) => getLandingPlanRank(a.id) - getLandingPlanRank(b.id));
}

function getLandingPlanRank(planId: string) {
  const index = landingPlanOrder.indexOf(planId);
  return index === -1 ? landingPlanOrder.length - 1 : index;
}

function getPublicPlanFeatures(plan: PlanPublic) {
  const features: string[] = [];
  if (plan.max_dashboards != null) features.push(`${formatLandingCompact(plan.max_dashboards)} dashboard${plan.max_dashboards === 1 ? '' : 's'}`);
  if (plan.max_devices != null) features.push(`${formatLandingCompact(plan.max_devices)} device${plan.max_devices === 1 ? '' : 's'}`);
  if (plan.max_data_streams != null) features.push(`${formatLandingCompact(plan.max_data_streams)} data stream${plan.max_data_streams === 1 ? '' : 's'}`);
  if (plan.max_users != null) features.push(`${formatLandingCompact(plan.max_users)} user${plan.max_users === 1 ? '' : 's'}`);
  for (const feature of plan.features || []) {
    const label = landingFeatureLabels[feature] || formatLandingTitle(feature);
    if (!features.includes(label)) features.push(label);
  }
  return features.length > 0 ? features.slice(0, 8) : ['Live monitoring workspace', 'Configurable alerts', 'Dashboard access'];
}

function getLandingPriceNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatLandingPrice(value: number, currency?: string | null) {
  if (value === 0) return 'Free';
  if (!currency || currency === 'USD') {
    return `$${value.toLocaleString(undefined, {
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    })}`;
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `${currency || '$'} ${value.toLocaleString()}`;
  }
}

function formatLandingCompact(value: number) {
  return new Intl.NumberFormat(undefined, { notation: value >= 1000 ? 'compact' : 'standard' }).format(value);
}

function formatLandingTitle(value: string) {
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const features = [
  {
    icon: CpuIcon,
    title: 'Device onboarding',
    body: 'Create direct, gateway, or virtual devices with managed credentials, parser mapping, and clear setup details for each data stream.',
    accent: 'cyan',
    span: 'wide',
  },
  {
    icon: GaugeIcon,
    title: 'Live dashboards',
    body: 'Track every site with live readings, configurable widgets, stale-device signals, and real-time operational cards.',
    accent: 'green',
  },
  {
    icon: ActivityIcon,
    title: 'Usage and performance analysis',
    body: 'Understand energy yield, battery trends, water or fuel usage, machine runtime, counters, and daily performance without exporting spreadsheets first.',
    accent: 'blue',
  },
  {
    icon: ShieldIcon,
    title: 'Alerts and escalation',
    body: 'Route threshold, stale-reading, and status alerts to operators through email, Telegram, and dashboard notifications.',
    accent: 'amber',
  },
  {
    icon: BoxesIcon,
    title: 'Asset maps',
    body: 'Visualize sites, areas, floor plans, GPS devices, and mapped parameters in one operational view.',
    accent: 'violet',
  },
  {
    icon: BarChartIcon,
    title: 'Reports and audit trail',
    body: 'Generate executive and operational reports, track user activity, and keep a clear record of platform changes.',
    accent: 'pink',
    span: 'wide',
  },
];

const useCases = [
  {
    icon: ZapIcon,
    title: 'Solar farms and hybrid power',
    body: 'Track site output, inverter data, availability, alarms, weather-linked performance, and generation trends across renewable assets.',
  },
  {
    icon: ActivityIcon,
    title: 'BESS / battery energy storage',
    body: 'Monitor battery state of charge, charge/discharge cycles, temperature, warnings, uptime, and storage performance in one workspace.',
  },
  {
    icon: GaugeIcon,
    title: 'Water, pumps, and utilities',
    body: 'Measure flow, pressure, tank levels, pump runtime, abnormal usage, and site status for municipal, estate, and industrial utilities.',
  },
  {
    icon: ShieldIcon,
    title: 'Cold chain and critical rooms',
    body: 'Watch temperature, humidity, power status, door events, and breach alerts for stores, warehouses, clinics, and production rooms.',
  },
  {
    icon: BoxesIcon,
    title: 'Manufacturing and equipment',
    body: 'Bring machine runtime, production conditions, vibration, power, counters, and maintenance signals into operational dashboards.',
  },
  {
    icon: BarChartIcon,
    title: 'Remote sites and asset networks',
    body: 'Unify telecom shelters, farms, depots, campuses, GPS assets, and distributed facilities without forcing every site into one industry template.',
  },
];

const workflowSteps = [
  {
    title: 'Connect the data',
    body: 'Bring in sensors, gateways, meters, inverters, controllers, machines, and mapped data streams.',
  },
  {
    title: 'Model the operation',
    body: 'Organize readings by site, asset, dashboard, alert rule, report, user role, and maintenance workflow.',
  },
  {
    title: 'Act early',
    body: 'Spot downtime risk, abnormal usage, stale telemetry, performance changes, and site issues before they become expensive.',
  },
];

const contactChannels = [
  {
    icon: PhoneIcon,
    label: 'Call support',
    value: '+234 807 974 4515',
    href: 'tel:+2348079744515',
    body: 'Speak with Seedronics about your sites, existing installation, or a planned rollout.',
  },
  {
    icon: MailIcon,
    label: 'Email sales and support',
    value: 'admin@seedronics.com',
    href: 'mailto:admin@seedronics.com?subject=Seedronics%20Cloud%20enquiry',
    body: 'Send your company details, device count, and the facilities you want to monitor.',
  },
  {
    icon: CalendarIcon,
    label: 'Book a demo meeting',
    value: 'See the platform in action',
    href: '#contact-demo',
    body: 'Walk through live dashboards, alerts, reports, maps, use cases, and predictive maintenance workflows.',
  },
];

function useLiveTelemetry() {
  const [tick, setTick] = useState(0);
  const historyRef = useRef<number[]>(Array.from({ length: 40 }, () => 40 + Math.random() * 30));

  useEffect(() => {
    const id = window.setInterval(() => {
      historyRef.current = [...historyRef.current.slice(1), 40 + Math.random() * 30];
      setTick((current) => current + 1);
    }, 900);

    return () => window.clearInterval(id);
  }, []);

  const points = historyRef.current;
  const path = points
    .map((value, index) => `${index === 0 ? 'M' : 'L'} ${((index / (points.length - 1)) * 300).toFixed(1)} ${(80 - value).toFixed(1)}`)
    .join(' ');

  return {
    path,
    activeAlerts: 7 + (tick % 4),
    devices: 126 + (tick % 9),
    readingsPerMin: Math.round(840 + Math.sin(tick / 3) * 42 + (tick % 13)),
    sites: 18 + (tick % 3),
  };
}

function useRevealOnScroll(refreshKey?: string) {
  useEffect(() => {
    const items = Array.from(document.querySelectorAll<HTMLElement>('[data-m19-reveal]:not(.is-visible)'));

    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-80px 0px', threshold: 0.08 }
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [refreshKey]);
}

function revealDelay(index: number) {
  return { '--m19-reveal-delay': `${index * 100}ms` } as React.CSSProperties;
}

export default function Module19Preview({
  authMode = 'modal',
  initialSection,
}: {
  authMode?: LandingAuthMode;
  initialSection?: LandingSection;
}) {
  const navigate = useNavigate();
  const [activeAuthMode, setActiveAuthMode] = useState<AuthMode | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [yearly, setYearly] = useState(true);
  const [publicPlans, setPublicPlans] = useState<PlanPublic[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const pricingPlans = useMemo(() => buildLandingPlans(publicPlans), [publicPlans]);
  const revealRefreshKey = useMemo(() => pricingPlans.map((plan) => plan.id).join('|'), [pricingPlans]);

  useRevealOnScroll(revealRefreshKey);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let mounted = true;
    setPlansLoading(true);
    getPublicPlans()
      .then((items) => {
        if (mounted) setPublicPlans(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        console.error('Failed to load public plans:', err.response?.data?.error || err.message);
        if (mounted) setPublicPlans([]);
      })
      .finally(() => {
        if (mounted) setPlansLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const hashSection = window.location.hash.replace('#', '');
    const targetSection = initialSection || (hashSection === 'use-cases' || hashSection === 'pricing' || hashSection === 'faq' || hashSection === 'contact' ? hashSection : '');
    if (!targetSection) return;
    window.setTimeout(() => {
      document.getElementById(targetSection)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }, [initialSection]);

  useEffect(() => {
    if (!activeAuthMode) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeAuthMode]);

  const openAuth = (mode: AuthMode) => {
    if (authMode === 'routes') {
      navigate(mode === 'signin' ? '/login' : '/signup');
      return;
    }
    setActiveAuthMode(mode);
  };

  return (
    <div className="m19-preview m19-cloud-exact">
      <header className={`m19-cloud-nav ${scrolled ? 'is-scrolled' : ''}`}>
        <nav className="m19-cloud-nav-inner">
          <a className="m19-cloud-brand" href="#top" aria-label="Seedronics Cloud">
            <img className="m19-cloud-brand-logo" src="/brand/seedronics-wordmark-light.png" alt="Seedronics" />
          </a>

          <div className="m19-cloud-nav-links">
            <a href="#features">Platform</a>
            <a href="#use-cases">Use cases</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <a href="#contact">Contact us</a>
          </div>

          <button className="m19-cloud-primary m19-cloud-nav-button" onClick={() => openAuth('signin')} type="button">
            Open Console <ArrowRightIcon />
          </button>
        </nav>
      </header>

      <main id="top">
        <Hero onOpenAuth={openAuth} />
        <Features />
        <UseCases />
        <Pricing plans={pricingPlans} plansLoading={plansLoading} onBillingChange={setYearly} onOpenAuth={openAuth} yearly={yearly} />
        <Faq />
        <Contact />
      </main>

      <Footer onOpenAuth={openAuth} />

      {activeAuthMode && authMode === 'modal' && (
        <AuthModal
          mode={activeAuthMode}
          onClose={() => setActiveAuthMode(null)}
          onModeChange={(nextMode) => setActiveAuthMode(nextMode)}
        />
      )}
    </div>
  );
}

function Hero({ onOpenAuth }: { onOpenAuth: (mode: AuthMode) => void }) {
  return (
    <section className="m19-cloud-hero">
      <div className="m19-cloud-grid" aria-hidden="true" />
      <div className="m19-cloud-blob m19-cloud-blob-cyan" aria-hidden="true" />
      <div className="m19-cloud-blob m19-cloud-blob-violet" aria-hidden="true" />

      <div className="m19-cloud-hero-inner">
        <div>
          <div className="m19-cloud-pill" data-m19-reveal style={revealDelay(0)}>
            <ZapIcon />
            Industrial telemetry, alerts, and reports in one workspace
          </div>

          <h1 className="m19-cloud-hero-title" data-m19-reveal style={revealDelay(1)}>
            Your sites and devices.
            <br />
            <span>One command center.</span>
          </h1>

          <p className="m19-cloud-hero-copy" data-m19-reveal style={revealDelay(2)}>
            Manage every site and facility from anywhere. Seedronics Cloud gives your team live visibility, clear
            performance analysis, early warning alerts, and predictive maintenance insights that help prevent costly
            downtime.
          </p>

          <div className="m19-cloud-actions" data-m19-reveal style={revealDelay(3)}>
            <button className="m19-cloud-primary" onClick={() => onOpenAuth('signup')} type="button">
              Start free trial <ArrowRightIcon />
            </button>
            <a className="m19-cloud-secondary" href="#contact">
              Book a demo
            </a>
          </div>

          <div className="m19-cloud-stats" data-m19-reveal style={revealDelay(5)}>
            <span><strong>30d</strong> trial workspace</span>
            <span><strong>24/7</strong> live telemetry</span>
            <span><strong>PDF</strong> operational reports</span>
          </div>
        </div>

        <div className="m19-cloud-hero-card-wrap">
          <TelemetryCard />
        </div>
      </div>
    </section>
  );
}

function TelemetryCard() {
  const { activeAlerts, devices, path, readingsPerMin, sites } = useLiveTelemetry();

  return (
    <div className="m19-cloud-telemetry" data-m19-reveal style={revealDelay(4)}>
      <div className="m19-cloud-card-glow" aria-hidden="true" />

      <div className="m19-cloud-card-content">
        <div className="m19-cloud-card-top">
          <div className="m19-cloud-live-label">
            <span className="m19-cloud-live-dot">
              <span />
              <i />
            </span>
            <span>Live &middot; data stream</span>
          </div>
          <RadioIcon />
        </div>

        <div className="m19-cloud-mini-grid">
          <div>
            <p>Sites monitored</p>
            <strong>{sites}</strong>
          </div>
          <div>
            <p>Devices online</p>
            <strong>{devices}</strong>
          </div>
          <div>
            <p>Active alerts</p>
            <strong>{activeAlerts}</strong>
          </div>
        </div>

        <div className="m19-cloud-chart-card">
          <div>
            <span>{readingsPerMin.toLocaleString()} readings / min</span>
            <ActivityIcon />
          </div>
          <svg viewBox="0 0 300 80" aria-label="Operational telemetry sparkline" role="img">
            <defs>
              <linearGradient id="m19CloudSpark" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#d4a853" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#d4a853" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`${path} L 300 80 L 0 80 Z`} fill="url(#m19CloudSpark)" />
            <path d={path} fill="none" stroke="#d4a853" strokeLinejoin="round" strokeWidth="1.8" />
          </svg>
        </div>

        <div className="m19-cloud-card-flags">
          <span><ShieldIcon /> Data connected</span>
          <span><ZapIcon /> Alerts active</span>
        </div>
      </div>
    </div>
  );
}

function Features() {
  return (
    <section id="features" className="m19-cloud-section m19-cloud-features">
      <div className="m19-cloud-container">
        <div className="m19-cloud-section-heading" data-m19-reveal>
          <p>The platform</p>
          <h2>
            Everything between your telemetry
            <br />
            and <span>your operations</span>
          </h2>
          <small>
            One control plane for the full lifecycle of industrial monitoring - from device onboarding to reports.
          </small>
        </div>

        <div className="m19-cloud-feature-grid">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <article
                className={`m19-cloud-feature-card is-${feature.accent} ${feature.span === 'wide' ? 'is-wide' : ''}`}
                data-m19-reveal
                key={feature.title}
                style={revealDelay(index % 3)}
              >
                <div className="m19-cloud-hover-wash" aria-hidden="true" />
                <span className="m19-cloud-feature-icon">
                  <Icon />
                </span>
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  return (
    <section id="use-cases" className="m19-cloud-section m19-cloud-usecases">
      <div className="m19-cloud-usecase-glow" aria-hidden="true" />

      <div className="m19-cloud-container">
        <div className="m19-cloud-section-heading is-centered" data-m19-reveal>
          <p>Use cases</p>
          <h2>
            Built for many kinds
            <br />
            of <span>operational data</span>
          </h2>
          <small>
            Seedronics is not limited to one industry. It gives teams a flexible way to monitor sites, assets,
            equipment, energy systems, and facilities from the same command center.
          </small>
        </div>

        <div className="m19-cloud-usecase-grid">
          {useCases.map((useCase, index) => {
            const Icon = useCase.icon;
            return (
              <article className="m19-cloud-usecase-card" data-m19-reveal key={useCase.title} style={revealDelay(index % 3)}>
                <span>
                  <Icon />
                </span>
                <h3>{useCase.title}</h3>
                <p>{useCase.body}</p>
              </article>
            );
          })}
        </div>

        <div className="m19-cloud-workflow" data-m19-reveal>
          {workflowSteps.map((step, index) => (
            <div key={step.title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <strong>{step.title}</strong>
              <p>{step.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({
  plans,
  plansLoading,
  yearly,
  onBillingChange,
  onOpenAuth,
}: {
  plans: LandingPlan[];
  plansLoading: boolean;
  yearly: boolean;
  onBillingChange: (yearly: boolean) => void;
  onOpenAuth: (mode: AuthMode) => void;
}) {
  return (
    <section id="pricing" className="m19-cloud-section m19-cloud-pricing">
      <div className="m19-cloud-price-glow" aria-hidden="true" />

      <div className="m19-cloud-container">
        <div className="m19-cloud-section-heading is-centered" data-m19-reveal>
          <p>Pricing</p>
          <h2>
            Start with a trial,
            <br />
            scale by <span>sites and devices</span>
          </h2>

          <div className="m19-cloud-billing" role="group" aria-label="Billing period">
            {(['Monthly', 'Yearly'] as const).map((label) => {
              const active = (label === 'Yearly') === yearly;
              return (
                <button
                  className={active ? 'is-active' : ''}
                  key={label}
                  onClick={() => onBillingChange(label === 'Yearly')}
                  type="button"
                >
                  {label}
                  {label === 'Yearly' && <span>-20%</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="m19-cloud-plan-grid">
          {plans.map((plan, index) => {
            const price = yearly ? plan.yearly : plan.monthly;
            return (
              <article
                className={`m19-cloud-plan-card ${plan.highlight ? 'is-highlighted' : ''} ${plan.id === 'custom' ? 'is-custom' : ''}`}
                data-m19-reveal
                key={plan.id}
                style={revealDelay(index)}
              >
                {plan.highlight && (
                  <span className="m19-cloud-plan-badge">
                    <SparklesIcon /> Most popular
                  </span>
                )}
                <h3>{plan.name}</h3>
                <p>{plan.tagline}</p>

                <div className="m19-cloud-price">
                  <strong>{plan.priceLabel ?? formatLandingPrice(price, plan.currency)}</strong>
                  <span>{plan.priceNote ?? `/ month${yearly && price > 0 ? ', billed yearly' : ''}`}</span>
                </div>

                <ul>
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <CheckIcon />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className={plan.highlight ? 'm19-cloud-primary' : 'm19-cloud-secondary'}
                  onClick={() => onOpenAuth('signup')}
                  type="button"
                >
                  {plan.ctaLabel ?? (plan.monthly === 0 ? 'Start for free' : `Choose ${plan.name}`)}
                </button>
              </article>
            );
          })}
        </div>

        <p className="m19-cloud-pricing-note">
          {plansLoading ? 'Loading live plan availability...' : 'Trial and plan limits can align with data-stream count, retention, reporting, support, and deployment scope.'}
        </p>
      </div>
    </section>
  );
}

function Faq() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="m19-cloud-section m19-cloud-faq">
      <div className="m19-cloud-container">
        <div className="m19-cloud-section-heading is-centered" data-m19-reveal>
          <p>FAQ</p>
          <h2>
            Answers before
            <br />
            <span>you start monitoring</span>
          </h2>
          <small>
            A quick guide for teams comparing plans, deployment options, and what a Seedronics demo can cover.
          </small>
        </div>

        <div className="m19-cloud-faq-grid">
          {faqItems.map((item, index) => (
            <div
              className="m19-cloud-faq-reveal"
              data-m19-reveal
              key={item.question}
              style={revealDelay(index % 3)}
            >
              <div className={`m19-cloud-faq-item ${openFaqIndex === index ? 'is-open' : ''}`}>
                <button
                  aria-controls={`m19-cloud-faq-answer-${index}`}
                  aria-expanded={openFaqIndex === index}
                  className="m19-cloud-faq-trigger"
                  onClick={() => setOpenFaqIndex((current) => current === index ? null : index)}
                  type="button"
                >
                  <span>{item.question}</span>
                  <PlusIcon />
                </button>
                <div
                  aria-hidden={openFaqIndex !== index}
                  className="m19-cloud-faq-answer"
                  id={`m19-cloud-faq-answer-${index}`}
                >
                  <div className="m19-cloud-faq-answer-inner">
                    <p>{item.answer}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitDemoRequest({ email, company, message: message || undefined });
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(
        err.response?.data?.error || 'Could not send your request right now — please email admin@seedronics.com directly.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="m19-cloud-section m19-cloud-contact">
      <div className="m19-cloud-contact-glow" aria-hidden="true" />

      <div className="m19-cloud-container">
        <div className="m19-cloud-contact-shell">
          <div className="m19-cloud-contact-copy" data-m19-reveal>
            <p className="m19-cloud-eyebrow">Contact us</p>
            <h2>
              Let us show you how Seedronics can work for your facilities.
            </h2>
            <p>
              Whether you monitor solar farms, BESS assets, water systems, cold rooms, manufacturing lines, fuel sites,
              generators, or remote facilities, our team can help you understand the right setup, walk you through the
              dashboard, and plan a rollout that fits your operations.
            </p>

            <div className="m19-cloud-contact-list">
              {contactChannels.map((channel, index) => {
                const Icon = channel.icon;
                return (
                  <a
                    className="m19-cloud-contact-link"
                    data-m19-reveal
                    href={channel.href}
                    key={channel.label}
                    style={revealDelay(index + 1)}
                  >
                    <span className="m19-cloud-contact-icon">
                      <Icon />
                    </span>
                    <span>
                      <small>{channel.label}</small>
                      <strong>{channel.value}</strong>
                      <em>{channel.body}</em>
                    </span>
                  </a>
                );
              })}
            </div>
          </div>

          <aside className="m19-cloud-contact-panel" data-m19-reveal id="contact-demo" style={revealDelay(2)}>
            <div className="m19-cloud-contact-panel-top">
              <span><HeadsetIcon /></span>
              <div>
                <p>Demo meeting</p>
                <h3>Request a guided walkthrough</h3>
              </div>
            </div>

            <p>
              We can demonstrate how Seedronics tracks sites, analyses consumption, highlights unusual behaviour, and
              supports predictive maintenance decisions before issues become expensive downtime.
            </p>

            <form className="m19-cloud-contact-form" onSubmit={handleSubmit}>
              <label>
                Work email
                <input
                  autoComplete="email"
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  required
                  type="email"
                  value={email}
                />
              </label>
              <label>
                Company or facility
                <input
                  autoComplete="organization"
                  onChange={(event) => setCompany(event.target.value)}
                  placeholder="Company name"
                  required
                  type="text"
                  value={company}
                />
              </label>
              <label>
                What would you like to monitor?
                <textarea
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Solar, BESS / battery storage, water systems, cold rooms, machines, assets, sites..."
                  rows={4}
                  value={message}
                />
              </label>

              {submitted && (
                <p className="m19-cloud-contact-status" role="status">
                  Thank you — we've received your request and sent a confirmation to your inbox. Our team will reach out shortly.
                </p>
              )}
              {submitError && (
                <p className="m19-cloud-contact-status" role="alert">
                  {submitError}
                </p>
              )}

              <button className="m19-cloud-primary" disabled={submitting || submitted} type="submit">
                {submitting ? 'Sending…' : submitted ? 'Request received' : 'Request demo meeting'} <ArrowRightIcon />
              </button>
            </form>

            <div className="m19-cloud-contact-direct">
              <a href="tel:+2348079744515">+234 807 974 4515</a>
              <span />
              <a href="mailto:admin@seedronics.com">admin@seedronics.com</a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Footer({ onOpenAuth }: { onOpenAuth: (mode: AuthMode) => void }) {
  return (
    <footer className="m19-cloud-footer">
      <div className="m19-cloud-container">
        <div className="m19-cloud-footer-cta" data-m19-reveal>
          <div className="m19-cloud-footer-blob is-left" aria-hidden="true" />
          <div className="m19-cloud-footer-blob is-right" aria-hidden="true" />
          <h2>
            Bring your first site online
            <br />
            <span>with Seedronics Cloud</span>
          </h2>
          <p>Create a workspace, connect your operational data streams, and see live telemetry, alerts, reports, and maps in one console.</p>
          <div className="m19-cloud-footer-actions">
            <button className="m19-cloud-primary" onClick={() => onOpenAuth('signup')} type="button">
              Start free trial <ArrowRightIcon />
            </button>
            <a className="m19-cloud-secondary" href="#contact">
              Contact us
            </a>
          </div>
          <p className="m19-cloud-footer-contact">
            Call <a href="tel:+2348079744515">+234 807 974 4515</a> or email{' '}
            <a href="mailto:admin@seedronics.com">admin@seedronics.com</a>
          </p>
        </div>

        <div className="m19-cloud-footer-bottom">
          <a className="m19-cloud-brand" href="#top" aria-label="Seedronics Cloud">
            <img className="m19-cloud-brand-logo is-footer" src="/brand/seedronics-wordmark-light.png" alt="Seedronics" />
          </a>
          <div>
            <a href="#features">Platform</a>
            <a href="#use-cases">Use cases</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <a href="#contact">Contact us</a>
            <button className="m19-cloud-text-link" onClick={() => onOpenAuth('signin')} type="button">Sign in</button>
          </div>
          <p>&copy; {new Date().getFullYear()} Seedronics. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

function AuthModal({
  mode,
  onClose,
  onModeChange,
}: {
  mode: AuthMode;
  onClose: () => void;
  onModeChange: (mode: AuthMode) => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const isSignup = mode === 'signup';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    firstFieldRef.current?.focus();

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mode, onClose]);

  const switchMode = (nextMode: AuthMode) => {
    setSubmitted(false);
    onModeChange(nextMode);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
  };

  return (
    <div
      className="m19-cloud-auth-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
      role="presentation"
    >
      <section
        aria-labelledby="m19-cloud-auth-title"
        aria-modal="true"
        className="m19-cloud-auth-modal"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <button className="m19-cloud-auth-close" onClick={onClose} type="button" aria-label="Close form">
          <XIcon />
        </button>

        <div className="m19-cloud-auth-copy">
          <img src="/brand/seedronics-wordmark-light.png" alt="Seedronics" />
          <span>{isSignup ? 'Get started' : 'Welcome back'}</span>
          <h2 id="m19-cloud-auth-title">
            {isSignup ? 'Start managing your facilities from anywhere' : 'Sign in to your command center'}
          </h2>
          <p>
            {isSignup
              ? 'Create a workspace for your sites, assets, alerts, analysis, reports, and maintenance planning.'
              : 'Continue monitoring your sites, alerts, reports, and maintenance priorities in one workspace.'}
          </p>

          <div className="m19-cloud-auth-value-grid">
            <span><ActivityIcon /> Clear analysis</span>
            <span><ShieldIcon /> Early warnings</span>
            <span><BarChartIcon /> Maintenance insight</span>
          </div>
        </div>

        <div className="m19-cloud-auth-panel">
          <div className="m19-cloud-auth-tabs" role="tablist" aria-label="Account access">
            <button
              aria-selected={isSignup}
              className={isSignup ? 'is-active' : ''}
              onClick={() => switchMode('signup')}
              role="tab"
              type="button"
            >
              Get started
            </button>
            <button
              aria-selected={!isSignup}
              className={!isSignup ? 'is-active' : ''}
              onClick={() => switchMode('signin')}
              role="tab"
              type="button"
            >
              Sign in
            </button>
          </div>

          <form className="m19-cloud-auth-form" onSubmit={handleSubmit}>
            {isSignup ? (
              <>
                <label>
                  Work email
                  <input ref={firstFieldRef} autoComplete="email" placeholder="admin@company.com" required type="email" />
                </label>
                <label>
                  Company or facility
                  <input autoComplete="organization" placeholder="Acme Energy Depot" required type="text" />
                </label>
                <label>
                  Phone number
                  <input autoComplete="tel" placeholder="+234..." type="tel" />
                </label>
                <label>
                  Password
                  <input autoComplete="new-password" minLength={8} placeholder="Minimum 8 characters" required type="password" />
                </label>
              </>
            ) : (
              <>
                <label>
                  Email
                  <input ref={firstFieldRef} autoComplete="email" placeholder="admin@company.com" required type="email" />
                </label>
                <label>
                  Password
                  <input autoComplete="current-password" placeholder="Your password" required type="password" />
                </label>
              </>
            )}

            {submitted && (
              <p className="m19-cloud-auth-status" role="status">
                Preview captured. The live form will connect to Seedronics account access.
              </p>
            )}

            <button className="m19-cloud-primary m19-cloud-auth-submit" type="submit">
              {isSignup ? 'Create workspace' : 'Sign in'} <ArrowRightIcon />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function IconBase({ children, ...props }: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" viewBox="0 0 24 24" {...props}>
      {children}
    </svg>
  );
}

function ArrowRightIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </IconBase>
  );
}

function XIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </IconBase>
  );
}

function ActivityIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M3 12h4l2-7 4 14 2-7h6" />
    </IconBase>
  );
}

function BarChartIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="M21 20H3" />
    </IconBase>
  );
}

function BoxesIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="m7.5 4 4.5 2.6L16.5 4" />
      <path d="m7.5 4-4 2.3v5.4l4 2.3 4.5-2.6V6.6Z" />
      <path d="m16.5 4 4 2.3v5.4l-4 2.3-4.5-2.6V6.6Z" />
      <path d="m7.5 14 4.5 2.6 4.5-2.6" />
      <path d="M12 16.6V22" />
    </IconBase>
  );
}

function CalendarIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect height="18" rx="2" width="18" x="3" y="4" />
      <path d="M3 10h18" />
      <path d="M8 14h.01" />
      <path d="M12 14h.01" />
      <path d="M16 14h.01" />
    </IconBase>
  );
}

function CheckIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="m5 12 4 4L19 6" />
    </IconBase>
  );
}

function CpuIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect height="12" rx="2" width="12" x="6" y="6" />
      <rect height="4" width="4" x="10" y="10" />
      <path d="M4 10h2M4 14h2M18 10h2M18 14h2M10 4v2M14 4v2M10 18v2M14 18v2" />
    </IconBase>
  );
}

function GaugeIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 14a8 8 0 0 1 16 0" />
      <path d="M12 14l4-4" />
      <path d="M5 19h14" />
    </IconBase>
  );
}

function HeadsetIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4 13a8 8 0 0 1 16 0" />
      <path d="M4 13v3a2 2 0 0 0 2 2h1v-7H6a2 2 0 0 0-2 2Z" />
      <path d="M20 13v3a2 2 0 0 1-2 2h-1v-7h1a2 2 0 0 1 2 2Z" />
      <path d="M16 18v1a3 3 0 0 1-3 3h-2" />
    </IconBase>
  );
}

function MailIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <rect height="16" rx="2" width="18" x="3" y="4" />
      <path d="m4 7 8 6 8-6" />
    </IconBase>
  );
}

function PhoneIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.9.3 1.9.6 2.9.7a2 2 0 0 1 1.7 2Z" />
    </IconBase>
  );
}

function PlusIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </IconBase>
  );
}

function RadioIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M4.9 19a10 10 0 0 1 0-14" />
      <path d="M7.8 16.2a6 6 0 0 1 0-8.4" />
      <path d="M16.2 7.8a6 6 0 0 1 0 8.4" />
      <path d="M19.1 5a10 10 0 0 1 0 14" />
      <circle cx="12" cy="12" r="1" />
    </IconBase>
  );
}

function ShieldIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-5" />
    </IconBase>
  );
}

function SparklesIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M12 3l1.7 4.5L18 9.2l-4.3 1.7L12 15l-1.7-4.1L6 9.2l4.3-1.7Z" />
      <path d="M19 14l.8 2.1L22 17l-2.2.9L19 20l-.9-2.1L16 17l2.1-.9Z" />
      <path d="M5 14l.8 2.1L8 17l-2.2.9L5 20l-.9-2.1L2 17l2.1-.9Z" />
    </IconBase>
  );
}

function ZapIcon(props: IconProps) {
  return (
    <IconBase {...props}>
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
    </IconBase>
  );
}
